import { useForm } from '@inertiajs/react'
import { HelpCircle, MessageSquare, Megaphone, Award } from 'lucide-react'
import { Modal } from '@/components/ui/Modal'
import { Input } from '@/components/ui/Input'
import { Textarea } from '@/components/ui/Textarea'
import { Button } from '@/components/ui/Button'
import { AttachmentPicker } from '@/components/ui/AttachmentPicker'
import type { PostCategory } from '@/types'

interface CreatePostModalProps {
  isOpen: boolean
  onClose: () => void
  categories: PostCategory[]
  // Publie dans l'espace partagé Médecins/Pharmaciens plutôt que dans la communauté
  interpro?: boolean
}

type PostType = 'question' | 'discussion' | 'announcement'

const POST_TYPES: { value: PostType; label: string; icon: typeof HelpCircle; description: string }[] = [
  {
    value: 'question',
    label: 'Question',
    icon: HelpCircle,
    description: 'Posez une question à la communauté'
  },
  {
    value: 'discussion',
    label: 'Discussion',
    icon: MessageSquare,
    description: 'Lancez une discussion ouverte'
  },
  {
    value: 'announcement',
    label: 'Annonce',
    icon: Megaphone,
    description: 'Partagez une information importante'
  }
]

export default function CreatePostModal({ isOpen, onClose, categories, interpro = false }: CreatePostModalProps) {
  const { data, setData, post, processing, errors, reset, transform } = useForm<{
    title: string
    body: string
    post_type: PostType
    category_id: string
    attachments: File[]
  }>({
    title: '',
    body: '',
    post_type: 'discussion',
    category_id: '',
    attachments: []
  })

  transform(d => ({
    post: {
      title: d.title,
      body: d.body,
      post_type: d.post_type,
      category_id: d.category_id,
      attachments: d.attachments,
      interpro
    }
  }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post('/posts', {
      forceFormData: data.attachments.length > 0,
      onSuccess: () => {
        reset()
        onClose()
      }
    })
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Créer une publication" size="lg">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Points indicator */}
        <div className="flex items-center gap-2 px-3 py-2 bg-tessan-cream rounded-lg text-sm">
          <Award className="w-4 h-4 text-tessan-primary" />
          <span className="text-tessan-primary font-medium">
            +25 points pour cette publication
          </span>
        </div>

        {errors.base && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2">
            <p className="text-sm font-medium text-red-700">Publication refusée</p>
            <p className="text-sm text-red-700">
              {Array.isArray(errors.base) ? errors.base.join(', ') : errors.base}
            </p>
          </div>
        )}

        {/* Post Type Selection */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-text-primary">
            Type de publication <span className="text-error">*</span>
          </label>
          <div className="grid grid-cols-3 gap-3">
            {POST_TYPES.map((type) => {
              const Icon = type.icon
              const isSelected = data.post_type === type.value
              return (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => setData('post_type', type.value)}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${
                    isSelected
                      ? 'border-tessan-primary bg-tessan-cream'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <Icon className={`w-5 h-5 mb-1 ${isSelected ? 'text-tessan-primary' : 'text-gray-400'}`} />
                  <p className={`font-medium text-sm ${isSelected ? 'text-tessan-primary' : 'text-gray-700'}`}>
                    {type.label}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {type.description}
                  </p>
                </button>
              )
            })}
          </div>
        </div>

        {/* Title */}
        <Input
          label="Titre"
          value={data.title}
          onChange={(e) => setData('title', e.target.value)}
          placeholder={data.post_type === 'question' ? 'Ex: Comment gérer un cas de...' : 'Titre de votre publication'}
          error={errors.title}
          required
          minLength={5}
          maxLength={200}
        />

        {/* Category */}
        {categories.length > 0 && (
          <div className="space-y-1">
            <label htmlFor="category" className="block text-sm font-medium text-text-primary">
              Catégorie
            </label>
            <select
              id="category"
              value={data.category_id}
              onChange={(e) => setData('category_id', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-tessan-green focus:border-transparent transition-colors"
            >
              <option value="">Aucune catégorie</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Body */}
        <Textarea
          label="Contenu"
          value={data.body}
          onChange={(e) => setData('body', e.target.value)}
          placeholder="Détaillez votre question ou votre message..."
          error={errors.body}
          required
          rows={6}
          minLength={10}
        />

        {/* Attachments */}
        <div className="space-y-1">
          <label className="block text-sm font-medium text-text-primary">
            Pièces jointes
          </label>
          <AttachmentPicker
            files={data.attachments}
            onFilesChange={(files) => setData('attachments', files)}
          />
          {errors.attachments && (
            <p className="text-sm text-red-600">{errors.attachments}</p>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
          <Button type="button" variant="outline" onClick={handleClose} disabled={processing}>
            Annuler
          </Button>
          <Button type="submit" disabled={processing}>
            {processing ? 'Publication...' : 'Publier'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}
